/**
 * =============================================================================
 * DEKOSTA — Icon & surface design system (lucide-react + semantik tokenlar)
 * =============================================================================
 *
 * **Yardımcılar:** `getIcon`, `getIconColor`, `getIconSize` — tek API.
 *
 * **Semantik renk (`SEMANTIC_ICON_COLOR`):** success, error, warning, primary, info, muted
 *
 * **Nadirlik (`getRarityVisual`):** `revealRarityKey` + `collectionUi.RARITY_UI` + tutarlı `glow` — common,
 * uncommon, rare, epic, legendary, mythic (ürünle uyumlu).
 *
 * **Ürün alias:** `COLLECTION_ICON`, `ITEM_ICON`, `CODE_ICON`, `LEADERBOARD_ICON`, `PROFILE_ICON`
 *
 * **Gruplar:** `navIcons`, `actionIcons`, `statusIcons`, `adminIcons`
 *
 * **Boyut:** `ICON_SIZE` + `getIconSize` (small | medium | large)
 *
 * **İkon + metin:** `LabeledIcon` — ikon tek başına yerine etiketle birlikte kullanın (admin listeleri).
 * =============================================================================
 */

import React from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  Box,
  Camera,
  CheckCircle2,
  ChevronRight,
  Coins,
  FileText,
  FolderOpen,
  Gauge,
  Gem,
  Home,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Menu,
  Package,
  Palette,
  Repeat,
  Settings,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  Trophy,
  Unlock,
  User,
  UserCircle,
  UserRound,
  Users,
  Users2,
  Layers,
  EyeOff,
  WifiOff,
  X,
} from 'lucide-react';

import { RARITY_UI, revealRarityKey } from './collectionUi';

// --- Ürün alias’ları ---
export const COLLECTION_ICON = Package;
export const CODE_ICON = KeyRound;
export const LEADERBOARD_ICON = Trophy;
export const PROFILE_ICON = User;

export const navIcons = {
  home: Home,
  code: CODE_ICON,
  collection: COLLECTION_ICON,
  leaderboard: LEADERBOARD_ICON,
  profile: PROFILE_ICON,
};

export const actionIcons = {
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  x: X,
  sparkles: Sparkles,
  star: Star,
  layoutDashboard: LayoutDashboard,
  chevronRight: ChevronRight,
  mail: Mail,
  logOut: LogOut,
  coins: Coins,
  userCircle: UserCircle,
  camera: Camera,
  lock: Lock,
  unlock: Unlock,
  alertTriangle: AlertTriangle,
};

/** Profil alt-sayfa ızgara linkleri — her rota için semantik ikon + token rengi */
export const profileNavIcons = {
  appearance: { icon: Palette, color: 'text-violet-300' },
  achievements: { icon: Award, color: 'text-amber-300' },
  friends: { icon: Users, color: 'text-sky-300' },
  wishlist: { icon: Star, color: 'text-yellow-300' },
  trades: { icon: Repeat, color: 'text-cyan-300' },
  guilds: { icon: Shield, color: 'text-indigo-300' },
  cosmetics: { icon: Sparkles, color: 'text-pink-300' },
  lifetime: { icon: Gem, color: 'text-emerald-300' },
  settings: { icon: Settings, color: 'text-zinc-300' },
  account: { icon: UserCircle, color: 'text-zinc-300' },
};

export const statusIcons = {
  loading: Loader2,
  inbox: Inbox,
  wifiOff: WifiOff,
  eyeOff: EyeOff,
  checkCircle: CheckCircle2,
};

/** Yönetim paneli — liste, hub kartları, kenar çubuğu */
export const adminIcons = {
  layoutDashboard: LayoutDashboard,
  users: Users,
  userRound: UserRound,
  target: Target,
  slidersHorizontal: SlidersHorizontal,
  folderOpen: FolderOpen,
  users2: Users2,
  layers: Layers,
  keyRound: KeyRound,
  package: Package,
  fileText: FileText,
  activity: Activity,
  gauge: Gauge,
  settings: Settings,
  gem: Gem,
  userCircle: UserCircle,
  box: Box,
  menu: Menu,
  home: Home,
};

/**
 * Semantik durum renkleri (ikon + vurgu metinleri).
 * Admin dashboard istatistik kartları için `ADMIN_STAT_COLOR` ile birlikte kullanılır.
 */
export const SEMANTIC_ICON_COLOR = {
  success: 'text-emerald-400',
  error: 'text-red-400',
  warning: 'text-amber-400',
  primary: 'text-violet-400',
  info: 'text-sky-400',
  muted: 'text-zinc-500',
};

/** Admin özet kartları — mevcut görünümü koruyan sabit palet */
export const ADMIN_STAT_COLOR = {
  users: 'text-blue-400',
  series: 'text-yellow-400',
  catalogChars: 'text-emerald-400',
  catalogItems: 'text-violet-400',
  codes: 'text-cyan-400',
  codesUsed: 'text-orange-400',
  codesUnused: 'text-green-400',
  codesDisabled: 'text-gray-400',
  boxes: 'text-green-400',
  boxesPrepared: 'text-emerald-400',
  boxesShipped: 'text-teal-400',
};

/**
 * Nadirlik — çerçeve/metin/rozet: `collectionUi`; dış halka parıltı: burada.
 * common / rare / epic / legendary ana tier; uncommon & mythic tam uyum için dahil.
 */
export const RARITY_GLOW = {
  common: 'shadow-[0_0_12px_rgba(156,163,175,0.18)]',
  uncommon: 'shadow-[0_0_14px_rgba(148,163,184,0.22)]',
  rare: 'shadow-[0_0_16px_rgba(59,130,246,0.32)]',
  epic: 'shadow-[0_0_18px_rgba(139,92,246,0.36)]',
  legendary: 'shadow-[0_0_20px_rgba(245,158,11,0.38)]',
  mythic: 'shadow-[0_0_22px_rgba(236,72,153,0.42)]',
};

/**
 * Koleksiyon / envanter kartı hover — nadirliğe göre derinlik artar (performans: saf Tailwind).
 */
export const RARITY_CARD_HOVER = {
  common:
    'hover:shadow-[0_12px_40px_-16px_rgba(0,0,0,0.9),0_0_20px_-12px_rgba(255,255,255,0.06)]',
  uncommon: 'hover:shadow-[0_12px_40px_-14px_rgba(148,163,184,0.28)]',
  rare: 'hover:shadow-[0_14px_44px_-12px_rgba(59,130,246,0.38)]',
  epic: 'hover:shadow-[0_14px_48px_-10px_rgba(139,92,246,0.42)]',
  legendary: 'hover:shadow-[0_16px_48px_-10px_rgba(245,158,11,0.38)]',
  mythic: 'hover:shadow-[0_16px_52px_-8px_rgba(244,114,182,0.4)]',
};

/**
 * Statik glow + hover — `getRarityVisual(rarity).glow` + tier hover.
 * @param {string} [rarity]
 */
export function getRarityCardExperienceClass(rarity) {
  const v = getRarityVisual(rarity);
  const hover = RARITY_CARD_HOVER[v.key] || RARITY_CARD_HOVER.common;
  return `${v.glow} ${hover}`.trim();
}

/**
 * @param {string} [rarity]
 * @returns {{ key: string, glow: string } & import('./collectionUi').RARITY_UI['common']}
 */
export function getRarityVisual(rarity) {
  const key = revealRarityKey(rarity);
  const base = RARITY_UI[key] || RARITY_UI.common;
  const glow = RARITY_GLOW[key] || RARITY_GLOW.common;
  return { key, glow, ...base };
}

// --- Deneyim katmanı: yüzey / toast / rozet / chip (Tailwind) ---
export const SEMANTIC_SURFACE = {
  toast: {
    success: 'bg-emerald-950/90 border border-emerald-500/35 text-emerald-50 shadow-[0_8px_32px_-12px_rgba(16,185,129,0.35)]',
    error: 'bg-red-950/90 border border-red-500/35 text-red-50 shadow-[0_8px_32px_-12px_rgba(239,68,68,0.35)]',
    warning: 'bg-amber-950/90 border border-amber-500/35 text-amber-50 shadow-[0_8px_32px_-12px_rgba(245,158,11,0.3)]',
    primary: 'bg-violet-950/90 border border-violet-500/35 text-violet-50 shadow-[0_8px_32px_-12px_rgba(139,92,246,0.35)]',
    info: 'bg-sky-950/90 border border-sky-500/35 text-sky-50 shadow-[0_8px_32px_-12px_rgba(14,165,233,0.3)]',
    muted: 'bg-zinc-900/95 border border-zinc-600/40 text-zinc-100',
  },
  badge: {
    success: 'bg-emerald-600/85 text-emerald-50 border border-emerald-400/30',
    error: 'bg-red-600/85 text-red-50 border border-red-400/30',
    warning: 'bg-amber-600/85 text-amber-950 border border-amber-400/35',
    primary: 'bg-violet-600/85 text-violet-50 border border-violet-400/35',
    info: 'bg-sky-600/85 text-sky-50 border border-sky-400/30',
    muted: 'bg-zinc-700/90 text-zinc-200 border border-zinc-500/35',
  },
  chip: {
    success: 'border-emerald-500/45 text-emerald-200 bg-emerald-950/40',
    error: 'border-red-500/45 text-red-200 bg-red-950/40',
    warning: 'border-amber-500/45 text-amber-200 bg-amber-950/35',
    primary: 'border-violet-500/45 text-violet-200 bg-violet-950/40',
    info: 'border-sky-500/45 text-sky-200 bg-sky-950/35',
    muted: 'border-zinc-600/50 text-zinc-300 bg-black/35',
  },
  border: {
    success: 'border-emerald-500/45',
    error: 'border-red-500/45',
    warning: 'border-amber-500/45',
    primary: 'border-violet-500/45',
    info: 'border-sky-500/45',
    muted: 'border-zinc-600/50',
  },
};

/** Boş / yükleme / hata / başarı blokları — konteyner + tipografi */
export const STATE_UI = {
  empty: {
    container: 'glass-effect rounded-2xl border border-violet-500/15 p-8 text-center',
    title: 'text-lg font-bold text-white',
    body: 'text-sm text-zinc-500 max-w-sm mx-auto leading-relaxed mt-2 break-words',
    icon: 'text-violet-400/80',
  },
  loading: {
    container: 'glass-effect rounded-2xl border border-white/10 p-6',
    title: 'text-sm text-gray-400',
    body: '',
    icon: 'text-violet-400',
  },
  error: {
    container: 'glass-effect rounded-2xl border border-amber-500/20 p-6 text-center',
    title: 'text-lg font-bold text-white',
    body: 'text-sm text-amber-200/80 max-w-sm mx-auto mt-2 break-words',
    icon: 'text-amber-400/90',
  },
  success: {
    container: 'glass-effect rounded-2xl border border-emerald-500/25 p-6 text-center',
    title: 'text-lg font-bold text-emerald-100',
    body: 'text-sm text-emerald-200/85 max-w-sm mx-auto mt-2 break-words',
    icon: 'text-emerald-400',
  },
};

/**
 * Ödül intro — Sparkles ikon sınıfı (RewardRevealModal intro sahnesi).
 * @param {string} rrk — `revealRarityKey` çıktısı
 */
export function getRewardRevealIntroSparkleClass(rrk) {
  const k = String(rrk || 'common');
  if (k === 'mythic' || k === 'legendary') return 'text-amber-200';
  if (k === 'epic' || k === 'rare') return 'text-violet-200';
  if (k === 'uncommon') return 'text-slate-200';
  return 'text-gray-300';
}

/**
 * Legendary / mythic için daha güçlü pulse; reduced motion’da statik.
 * @param {string} rrk
 * @param {boolean} reduced
 */
export function getRewardIntroSparkleMotion(rrk, reduced) {
  if (reduced) {
    return { animate: { scale: 1, rotate: 0 }, transition: { duration: 0 } };
  }
  const k = String(rrk || 'common');
  if (k === 'legendary') {
    return {
      animate: { scale: [1, 1.28, 1], rotate: [0, 11, -11, 0] },
      transition: { duration: 1.35, repeat: Infinity, ease: 'easeInOut' },
    };
  }
  if (k === 'mythic') {
    return {
      animate: { scale: [1, 1.2, 1], rotate: [0, 9, -9, 0] },
      transition: { duration: 1.55, repeat: Infinity, ease: 'easeInOut' },
    };
  }
  return {
    animate: { scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] },
    transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
  };
}

/** Intro orb — nadirlik glow’u Sparkles çevresinde */
export function getRewardIntroOrbWrapperClass(rrk) {
  const v = getRarityVisual(rrk);
  return `${v.glow} rounded-full p-3`.trim();
}

export const ICON_SIZE = {
  small: 'w-4 h-4 shrink-0',
  medium: 'w-5 h-5 shrink-0',
  large: 'w-7 h-7 shrink-0',
};

/** @param {'small'|'medium'|'large'} size */
export function getIconSize(size) {
  return ICON_SIZE[size] || ICON_SIZE.small;
}

/** Geriye dönük uyumluluk */
export function iconSizeClass(key) {
  return getIconSize(key);
}

export const EVENT_TONE = {
  reward: 'text-violet-300',
  equip: 'text-blue-400',
  achievement: 'text-amber-400',
};

export const feedEventIconComponents = {
  activation_character: UserCircle,
  activation_item: Gem,
  rare_drop: Sparkles,
  item_equip: Shield,
  star_progress: Star,
  collection_score_milestone: Trophy,
  achievement_unlocked: Award,
  default: Sparkles,
};

const feedEventIconTextClass = {
  activation_character: 'text-violet-400',
  activation_item: EVENT_TONE.reward,
  rare_drop: EVENT_TONE.reward,
  item_equip: EVENT_TONE.equip,
  star_progress: 'text-amber-400',
  collection_score_milestone: 'text-amber-500',
  achievement_unlocked: EVENT_TONE.achievement,
  default: 'text-violet-400/80',
};

export function getFeedEventIconComponent(eventType) {
  const k = eventType == null ? '' : String(eventType);
  return feedEventIconComponents[k] || feedEventIconComponents.default;
}

function buildIconRegistry() {
  const out = { ...feedEventIconComponents };
  const add = (prefix, obj) => {
    Object.entries(obj).forEach(([k, v]) => {
      out[`${prefix}.${k}`] = v;
    });
  };
  add('nav', navIcons);
  add('action', actionIcons);
  add('status', statusIcons);
  add('admin', adminIcons);
  return out;
}

const ICON_REGISTRY = buildIconRegistry();

/**
 * Birleşik ikon çözümleyici.
 * @param {string} type — `nav.home`, `admin.users`, `action.x` veya ham `event_type` (örn. `item_equip`)
 */
export function getIcon(type) {
  if (type == null || type === '') return Sparkles;
  const s = String(type);
  if (ICON_REGISTRY[s]) return ICON_REGISTRY[s];
  return getFeedEventIconComponent(s);
}

/**
 * İkon rengi — semantik anahtar, feed olayı veya `rarity.<tier>`.
 * @param {string} ref
 */
export function getIconColor(ref) {
  if (ref == null || ref === '') return SEMANTIC_ICON_COLOR.muted;
  const s = String(ref);
  if (SEMANTIC_ICON_COLOR[s]) return SEMANTIC_ICON_COLOR[s];
  if (ADMIN_STAT_COLOR[s]) return ADMIN_STAT_COLOR[s];
  if (feedEventIconTextClass[s]) return feedEventIconTextClass[s];
  if (s.startsWith('rarity.')) {
    const tier = s.slice(7);
    return RARITY_UI[tier]?.text || SEMANTIC_ICON_COLOR.muted;
  }
  return feedEventIconTextClass[s] || SEMANTIC_ICON_COLOR.muted;
}

export function FeedEventIcon({ eventType, className, size }) {
  const Icon = getFeedEventIconComponent(eventType);
  const k = eventType == null ? '' : String(eventType);
  const color = feedEventIconTextClass[k] || feedEventIconTextClass.default;
  const resolved =
    className != null && String(className).trim() !== ''
      ? className
      : size
        ? getIconSize(size)
        : ICON_SIZE.small;
  return <Icon className={`${resolved} ${color}`.trim()} aria-hidden />;
}

/**
 * İkon + metin — erişilebilirlik ve tutarlı aralık (admin satır / buton içi).
 * @param {{ icon: React.ComponentType<{ className?: string }>, label: React.ReactNode, iconClassName?: string, textClassName?: string, className?: string, as?: keyof JSX.IntrinsicElements }} props
 */
export function LabeledIcon({
  icon: Icon,
  label,
  iconClassName = '',
  textClassName = 'text-sm font-medium text-white',
  className = 'inline-flex items-center gap-2 min-w-0',
  as: Tag = 'span',
}) {
  return (
    <Tag className={className}>
      <Icon className={iconClassName || `${getIconSize('small')} ${SEMANTIC_ICON_COLOR.muted}`} aria-hidden />
      <span className="min-w-0 truncate">{label}</span>
    </Tag>
  );
}
